"use client"

import { useState, useMemo, useEffect } from 'react';
import useAuthStore from '@/store/useAuthStore';
import useUserAvatarStore from '@/store/useUserAvatarStore';
import STORAGE_KEYS from '@/constant/storage';
import { createPortal } from 'react-dom';

const FAKE_EMAIL_DOMAIN = 'fakemail.pengi.ai';
import { LogOut } from 'lucide-react';
import supabase from '@/lib/supabase';
import report, { ReportEvent } from '@/lib/report';
import { LetterAvatar } from './Avatar';
import BookshelfUpdateReminder from './Popup/BookshelfUpdateReminder';
import { Quest } from '@/constant/schema';
import { addDiamond } from '@/constant/reward';
import { playCoinSound } from '@/lib/systemSound';
import { getCanonicalProfileRole } from '@/lib/profile-role';
import StudentProfileModal, { StudentProfileData, StudentProfileSaveData } from './StudentProfileModal';
import LinkParentAccountModal from '@/components/LinkParentAccountModal';
import SwitchToParentAccountModal from '@/components/SwitchToParentAccountModal';
import SubjectManagementModal from '@/components/SubjectManagementModal';
import JoinClassCodeModal from '@/components/JoinClassCodeModal';
import useTeacherClassInfo from '@/hooks/useTeacherClassInfo';

export interface UserAvatarProps {
  className?: string;
  allQuests?: Quest[] | null;
  onManageStudents?: () => void;
}

function formatRoleLabel(role: string) {
  if (role === 'parent_v2') return 'Parent';
  if (role === 'teacher') return 'Teacher';
  if (role === 'student') return 'Student';
  if (!role) return 'Unknown';
  return role
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function UserAvatar({ className, allQuests, onManageStudents }: UserAvatarProps) {
  const { user, signOut, updateProfile: updateAuthProfile, profile } = useAuthStore();
  const { isOpen, openedFromProfileCard, openProfile, closeProfile, resetProfileCardFlag } = useUserAvatarStore();

  const [showSwitchParentConfirm, setShowSwitchParentConfirm] = useState(false);
  const [showBindParentHelp, setShowBindParentHelp] = useState(false);
  const [showJoinClassModal, setShowJoinClassModal] = useState(false);
  const [showSubjectManagement, setShowSubjectManagement] = useState(false);
  const [showBookshelfReminder, setShowBookshelfReminder] = useState(false);
  const [changedFields, setChangedFields] = useState<{ grade?: boolean; school?: boolean }>({});
  const [hasParent, setHasParent] = useState(false);

  const canonicalRole = useMemo(() => getCanonicalProfileRole(profile), [profile]);
  const isStudentAccount = canonicalRole === 'student';
  const isParentAccount = canonicalRole === 'parent_v2';
  const roleLabel = formatRoleLabel(canonicalRole);
  const { hasTeacherClass, teacherClassInfo } = useTeacherClassInfo(isOpen);

  useEffect(() => {
    if (!isOpen || !isStudentAccount || !user?.id) return;
    supabase
      .from('parent_child_relationships')
      .select('parent_id', { count: 'exact', head: true })
      .eq('child_id', user.id)
      .then(({ count }) => setHasParent((count ?? 0) > 0));
  }, [isOpen, isStudentAccount, user?.id]);

  const toggleDropdown = () => {
    report(ReportEvent.USER_QUEST_AVATAR_CLICK, {});
    if (isOpen) {
      closeProfile();
    } else {
      openProfile();
    }
  };

  const getDisplayedLoginIdentifier = () => {
    const profileLoginName = String((profile as any)?.student_login_name || '').trim();
    if (profileLoginName) return profileLoginName;

    const rawEmail = String(user?.email || profile?.email || '').trim();
    if (!rawEmail) return '';

    const normalizedEmail = rawEmail.toLowerCase();
    const fakeSuffix = `@${FAKE_EMAIL_DOMAIN}`;
    if (normalizedEmail.endsWith(fakeSuffix)) {
      return rawEmail.slice(0, rawEmail.length - fakeSuffix.length);
    }
    return rawEmail;
  };

  const getUserDisplayName = () => {
    if (isParentAccount) {
      if (profile?.nickname) return profile.nickname;
      if (user?.user_metadata?.nickname) return user.user_metadata.nickname;
      if (user?.email) return user.email.split('@')[0];
      return 'Parent';
    }
    if (profile?.onboard_data?.profile?.name) return profile.onboard_data.profile.name;
    if (profile?.onboard_data?.profile?.firstName) return profile.onboard_data.profile.firstName;
    if (profile?.nickname) return profile.nickname;
    if (user?.user_metadata?.nickname) return user.user_metadata.nickname;
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  const profileData: StudentProfileData = {
    name: getUserDisplayName(),
    loginIdentifier: getDisplayedLoginIdentifier(),
    birthYear: profile?.onboard_data?.profile?.birthYear || '',
    grade: profile?.onboard_data?.profile?.grade || '',
    state: profile?.onboard_data?.profile?.state || null,
    school: profile?.onboard_data?.profile?.school || null,
    freeSubject: profile?.free_subject ?? null,
  };

  const handleSave = async (data: StudentProfileSaveData) => {
    report(ReportEvent.USER_QUEST_AVATAR_EDIT_SAVE_CLICK, {
      type: openedFromProfileCard ? 'profile_card' : 'default',
    });

    const oldGrade = profile?.onboard_data?.profile?.grade;
    const oldSchool = profile?.onboard_data?.profile?.school?.school_name;
    const gradeChanged = oldGrade !== data.grade;
    const schoolChanged = oldSchool !== data.school?.school_name;

    const updatedOnboardData = {
      ...profile?.onboard_data,
      profile: {
        ...profile?.onboard_data?.profile,
        name: data.name,
        school: data.school,
        birthYear: data.birthYear,
        grade: data.grade,
        state: data.state,
      },
    };

    await updateAuthProfile({ nickname: data.name, onboard_data: updatedOnboardData });

    // Handle password change via supabase directly (student owns their session)
    if (data.newPassword) {
      const { error } = await supabase.auth.updateUser({ password: data.newPassword });
      if (error) throw new Error(error.message);
    }

    if (openedFromProfileCard) {
      try {
        playCoinSound();
        setTimeout(() => playCoinSound(), 250);
        setTimeout(() => playCoinSound(), 500);
        await addDiamond(5);
      } catch {
        // non-fatal
      }
      resetProfileCardFlag();
    }

    if (isStudentAccount && (gradeChanged || schoolChanged)) {
      setChangedFields({ grade: gradeChanged, school: schoolChanged });
      closeProfile();
      setTimeout(() => setShowBookshelfReminder(true), 100);
    }
  };

  const handleClose = () => {
    closeProfile();
  };

  const handleManageSubject = () => {
    closeProfile();
    setTimeout(() => setShowSubjectManagement(true), 100);
  };

  if (!user) return null;

  const footerTrailingAction = (
    <button
      onClick={handleSignOut}
      className="display-text-semi inline-flex h-[31px] items-center justify-center gap-1 rounded-[11px] border border-[#E5E7EB] bg-white px-3 text-[10px] text-[#6B7280] shadow-sm transition-all hover:text-[#4B5563]"
    >
      <LogOut size={12} color={'currentColor'} />
      Log Out
    </button>
  );

  const footerLeadingAction = (
    <button
      onClick={() => {
        closeProfile();
        setTimeout(() => setShowBindParentHelp(true), 100);
      }}
      className="display-text-semi mt-[10px] inline-flex h-[42px] w-full items-center justify-center rounded-full bg-[#FFF3CC] px-3 text-[12px] text-[#D48A00] transition-colors hover:bg-[#FFE8A3]"
    >
      Link Parent Account
    </button>
  );

  return (
    <div>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center focus:outline-none"
      >
        <LetterAvatar backgroundColor={'#805ad5'} />
      </button>

      {isOpen && (
        <StudentProfileModal
          mode="student"
          profileData={profileData}
          onSave={handleSave}
          onClose={handleClose}
          onManageSubject={handleManageSubject}
          teacherClassName={teacherClassInfo?.teacherName || null}
          onJoinClass={() => {
            closeProfile();
            setTimeout(() => setShowJoinClassModal(true), 100);
          }}
          footerLeadingAction={footerLeadingAction}
          footerTrailingAction={footerTrailingAction}
          roleLabel={roleLabel}
        />
      )}

      <SubjectManagementModal
        visible={showSubjectManagement}
        onClose={() => setShowSubjectManagement(false)}
      />

      <SwitchToParentAccountModal
        visible={showSwitchParentConfirm}
        onClose={() => setShowSwitchParentConfirm(false)}
      />

      <LinkParentAccountModal
        visible={showBindParentHelp}
        onClose={() => setShowBindParentHelp(false)}
      />

      <JoinClassCodeModal
        visible={showJoinClassModal}
        onClose={() => setShowJoinClassModal(false)}
        persistDismissal={false}
        secondaryLabel="Cancel"
        replaceExisting={hasTeacherClass}
      />

      {/* Bookshelf Update Reminder */}
      {showBookshelfReminder && typeof window !== 'undefined' && createPortal(
        <BookshelfUpdateReminder
          visible={showBookshelfReminder}
          onClose={() => {
            setShowBookshelfReminder(false);
            closeProfile();
          }}
          changedFields={changedFields}
          allQuests={allQuests}
        />,
        document.body,
      )}
    </div>
  );

  async function handleSignOut() {
    report(ReportEvent.USER_QUEST_AVATAR_SIGN_OUT_CLICK, {});
    await signOut();
    localStorage.removeItem(STORAGE_KEYS.ONBOARD_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER);
    closeProfile();
    window.location.href = '/';
  }
}
